<template>
  <DsCard
    variant="elevated"
    padding="md"
    class="relative flex cursor-pointer flex-col transition-transform duration-100 active:scale-[0.97] motion-reduce:transition-none"
    :class="{ 'opacity-70': isRedeemed || isExpired || isCancelled }"
    role="button"
    tabindex="0"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
    @keydown.space.prevent="$emit('click')"
  >
    <div class="flex min-w-0 flex-col gap-1">
      <!-- Name and status on the first line: the name identifies the voucher and
           the status qualifies it, so they belong together. Nothing else can
           push the tag off — it is the shortest thing on the card. -->
      <div class="flex flex-row items-start gap-3">
        <div class="text-text-body-md min-w-0 flex-1 truncate font-bold text-grey-100">
          {{ voucher.name }}
        </div>
        <DsTag size="sm" :variant="statusTagVariant" class="text-label2 flex-shrink-0">
          {{ statusLabel }}
        </DsTag>
      </div>

      <div class="text-text-body-small-md font-semibold text-grey-100">{{ voucher.value }}</div>

      <!-- Only when there is something to say. A voucher with no deadline used to
           announce "Does not expire", which spent a line on the absence of news.
           `pr-6` keeps a long line clear of the chevron pinned beside it. -->
      <div v-if="expiryText" class="text-text-body-small truncate pr-6 text-grey-60">
        {{ expiryText }}
      </div>
    </div>

    <!-- Pinned to the corner rather than sitting in the last row: on a card with
         no expiry line it would otherwise claim a row of its own. Small and pale
         on purpose — it says the card opens, which the card already implies. -->
    <DsIcon
      name="chevron-right"
      size="sm"
      class="absolute bottom-3 right-3 text-grey-40"
      aria-hidden="true"
    />
  </DsCard>
</template>

<script>
import { Card as DsCard, Icon as DsIcon, Tag as DsTag } from '@team-hogc/hogc-one-design-system';
import {
  expiryText,
  isCancelled,
  isExpired,
  isRedeemed,
  statusLabel,
  statusTagVariant,
} from '@/utils/voucher-expiry';

export default {
  components: { DsCard, DsIcon, DsTag },
  props: {
    voucher: {
      type: Object,
      required: true,
    },
  },
  emits: ['click'],
  computed: {
    // Thin pass-throughs. The ladder itself lives in @/utils/voucher-expiry so
    // the card and the detail screen cannot disagree about the same voucher —
    // they had, twice, and copying the ladder into both was what let it happen.
    isRedeemed() {
      return isRedeemed(this.voucher);
    },
    isCancelled() {
      return isCancelled(this.voucher);
    },
    isExpired() {
      return isExpired(this.voucher);
    },
    statusLabel() {
      return statusLabel(this.voucher);
    },
    statusTagVariant() {
      return statusTagVariant(this.voucher);
    },
    expiryText() {
      return expiryText(this.voucher);
    },
  },
};
</script>
